"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { LightTheme, DarkTheme, BaseProvider } from "baseui";
import { Provider as StyletronProvider } from "styletron-react";
import { styletron } from "@src/common/lib/styletron";

export enum Theme {
  AUTO = "auto",
  LIGHT = "light",
  DARK = "dark",
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: Theme.LIGHT | Theme.DARK;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom theme overrides for Base UI
const customLightTheme = {
  ...LightTheme,
  colors: {
    ...LightTheme.colors,
    backgroundPrimary: "#ffffff",
    backgroundSecondary: "#f7f7f7",
    contentPrimary: "#000000",
    contentSecondary: "#666666",
  },
};

const customDarkTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    backgroundPrimary: "#1a1a1a",
    backgroundSecondary: "#2d2d2d",
    contentPrimary: "#ffffff",
    contentSecondary: "#a0a0a0",
  },
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(Theme.LIGHT);
  const [effectiveTheme, setEffectiveTheme] = useState<
    Theme.LIGHT | Theme.DARK
  >(Theme.LIGHT);

  useEffect(() => {
    // Check for saved theme preference
    const savedTheme = localStorage.getItem("theme") as Theme;
    if (savedTheme) {
      setThemeState(savedTheme);
    }

    // Function to update theme based on current settings
    const updateTheme = () => {
      const currentTheme: Theme =
        (localStorage.getItem("theme") as Theme) || Theme.LIGHT;
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? Theme.DARK
        : Theme.LIGHT;

      // If theme is auto, use system theme, otherwise use saved theme
      const newEffectiveTheme =
        currentTheme === Theme.AUTO ? systemTheme : currentTheme;
      document.documentElement.classList.toggle(
        "dark",
        newEffectiveTheme === Theme.DARK
      );
      setEffectiveTheme(newEffectiveTheme);
    };

    // Initial theme update
    updateTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const currentTheme = localStorage.getItem("theme") as Theme;
      if (currentTheme === Theme.AUTO) {
        updateTheme();
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);

    // Update theme immediately
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? Theme.DARK
      : Theme.LIGHT;
    const newEffectiveTheme = newTheme === Theme.AUTO ? systemTheme : newTheme;
    document.documentElement.classList.toggle(
      "dark",
      newEffectiveTheme === Theme.DARK
    );
    setEffectiveTheme(newEffectiveTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
      <StyletronProvider value={styletron}>
        <BaseProvider
          theme={
            effectiveTheme === Theme.DARK ? customDarkTheme : customLightTheme
          }
        >
          {children}
        </BaseProvider>
      </StyletronProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
